import { PanelDirection, PanelStatus } from './types';

// Copy is fixed by the ticket (Asana 1218414752691778) and is final: do not
// reword it. "in flight" appears here, in the two In Transit headings, and
// nowhere else in the app; everywhere else the state is called "in transit".

export interface EmptyStateCopy {
  heading: string;
  // Absent for Completed: its old body described the flight-path chart the
  // partner map replaced (Asana 1218718074971774), and was dropped, not reworded
  body?: string;
  cta?: { label: string; to: string };
}

export const EMPTY_STATE_COPY: Record<PanelDirection, Record<PanelStatus, EmptyStateCopy>> = {
  inbound: {
    ready: {
      heading: 'Nothing waiting to ship',
      body: 'Packages appear here when a trade is confirmed and the sender starts packing.',
      cta: { label: 'Add cards to my want list', to: '/my-cards/want-list' },
    },
    transit: {
      heading: 'Nothing in flight.',
      body: 'Packages appear here the moment a sender marks them shipped.',
    },
    completed: {
      heading: 'No completed trades yet',
    },
    cancelled: {
      heading: 'No cancellations',
      body: 'Cancelled packages would appear greyed out alongside the ones that landed.',
    },
  },
  outbound: {
    ready: {
      heading: 'Nothing to ship right now',
      body: 'Build a package and the points land when it is delivered.',
      cta: { label: 'Send cards', to: '/sendcards' },
    },
    transit: {
      heading: 'Nothing in flight.',
      body: 'Packages appear here once you mark them shipped.',
    },
    completed: {
      heading: 'No completed trades yet',
    },
    cancelled: {
      heading: 'No cancellations',
      body: 'Cancelled packages would appear greyed out alongside the ones that landed.',
    },
  },
};

/**
 * Leading sentence of the outbound Ready body. The count is live; the sentence
 * is omitted entirely when it is zero or unknown.
 */
export const sendableSentence = (count: number | null | undefined): string | null =>
  count && count > 0
    ? `${count.toLocaleString('en-US')} of your cards are on somebody's want list.`
    : null;

const FOOTNOTE_MIN_SAMPLE = 3;

/**
 * In Transit footnote. Numbers are live from the last 20 completed trades;
 * sampleSize is how many of those had a shipped time, so it is often under 20.
 */
export const transitFootnote = (history: {
  transitDaysMin: number | null;
  transitDaysMax: number | null;
  sampleSize: number;
}): string | null => {
  const { transitDaysMin, transitDaysMax, sampleSize } = history;
  // Under three deliveries the sentence reads as a claim about one package
  // ("Your last 1 landed in..."), so it is left out until there is a range.
  if (sampleSize < FOOTNOTE_MIN_SAMPLE || transitDaysMin == null || transitDaysMax == null) return null;
  // A same-day delivery comes back as 0; the chart counts any leg as at least a day
  const min = Math.max(1, transitDaysMin);
  const max = Math.max(1, transitDaysMax);
  const range = min === max ? `${min} ${min === 1 ? 'day' : 'days'}` : `${min} to ${max} days`;
  return `Your last ${sampleSize} landed in ${range}.`;
};
